/**
 * Containers: where encryption keys, API tokens and firewalls live. An API token never sees outside its
 * own; the console sees them all. Every tenant has a default container, which can be
 * renamed and never deleted.
 */
export const containers = {
  title: 'Containers',
  description:
    'Encryption keys, API tokens and firewalls each live in one container. An API token can never use an encryption key in another container. This dashboard sees them all.',

  switcher: {
    label: 'Container',
  },

  loading: 'Loading containers',
  loadFailed: 'The containers could not be read.',
  /** Over a list already on screen, which stays: a failed refetch does not blank it. */
  refreshFailed: 'The containers could not be refreshed. This is the last list read.',

  columns: {
    name: 'Name',
    keys: 'Encryption keys',
    apiTokens: 'API tokens',
    firewalls: 'Firewalls',
    created: 'Created',
    /** The row actions' head: hidden, but a screen reader's column has a name. */
    actions: 'Actions',
  },
  defaultBadge: 'Default',

  create: 'Create container',
  createTitle: 'Create a container',
  nameLabel: 'Name',
  createNote: 'It starts empty. Encryption keys, API tokens and firewalls made on its pages live in it for good; nothing moves between containers.',

  rename: 'Rename',
  /** Each row's buttons by name, beginning with the visible word, as on the API tokens page. */
  renameLabel: (name: string) => `Rename ${name}`,
  deleteLabel: (name: string) => `Delete ${name}`,
  renameTitle: (name: string) => `Rename "${name}"`,
  renameConfirm: 'Rename',
  renamed: (from: string, to: string) => `Renamed "${from}" to "${to}".`,

  delete: 'Delete',
  deleteTitle: (name: string) => `Delete "${name}"`,
  deleteConfirm: 'Delete container',
  deleteNote:
    'Only an empty container can be deleted: no encryption keys, pending deletion included, no live API tokens and no firewalls. Its name is free again at once.',
  /** The counts as the page sees them, which the API checks again. */
  holds: (keys: number, apiTokens: number, firewalls: number) =>
    `It holds ${keys} ${keys === 1 ? 'encryption key' : 'encryption keys'}, ${apiTokens} live API ${apiTokens === 1 ? 'token' : 'tokens'} and ${firewalls} ${firewalls === 1 ? 'firewall' : 'firewalls'}.`,
  deleted: (name: string) => `Deleted "${name}".`,
  failed: 'The container was not changed.',

  /** The flat paths and a stale container id land here only for the moment it takes to
   *  read the list and go on. */
  finding: 'Finding the container',
} as const;
